import React, { useState } from 'react';
import { connect } from 'react-redux';

import { toggleSnackBar } from 'apps/auth/actions/appAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import { DetailsItem } from 'apps/auth/components/common/DetailsItem/DetailsItem';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { Row } from 'apps/auth/components/common/Layout/Row';
import { Text } from 'apps/auth/components/common/Text/Text';
import PaymentStatus from 'apps/auth/components/status/PaymentStatus';
import BasePage from 'apps/auth/components/utils/BasePage';
import { DELIVERY_BANK_ACCOUNT } from 'apps/auth/constants/Constants';
import { logInfo } from 'apps/auth/helpers/clientLogger';
import { copyTextToClipboard, getFormattedCardOrAccountNumber, getReference } from 'apps/auth/helpers/utils';
import { useExtra, usePageMessages } from 'apps/auth/hooks/selectors';

const GTAccountDetailsPage = (props) => {
    // Available values are: null, 'notNow' and 'iPaid'.
    const [statusPage, setStatusPage] = useState(null);
    const {
        notNowButton,
        textShareDescription,
        amountDescription,
        title,
        paidButton,
        titleNotNow,
        subTitleNotNow,
        titleIPaid,
        subTitleIPaid,
        gotIt,
        bankTransferReferenceInfo,
        bankTransferReferenceAdditionalInfo,
        ...messages
    } = usePageMessages();
    const { iban, bic, amount, sharedDetails, companyName, reference, paymentId } = useExtra();
    const formattedIban = getFormattedCardOrAccountNumber(iban, DELIVERY_BANK_ACCOUNT);
    const formattedBic = getFormattedCardOrAccountNumber(bic, DELIVERY_BANK_ACCOUNT);

    const statusPageMessagesMap = {
        notNow: { title: titleNotNow, body: subTitleNotNow, icon: 'zap-80' },
        iPaid: { title: titleIPaid, body: subTitleIPaid, icon: 'clock-80' },
    };

    const renderField = (label, value, additionalInfo = '') => {
        const copyOnClick = (e) => {
            e.preventDefault();
            const result = copyTextToClipboard(value);
            if (result) {
                props.toggleSnackBar(true, `${label} ${messages.copied}`);
            }
        };

        return (
            <Row indent="24px">
                <DetailsItem
                    detailsTitle={label}
                    detailsText={value}
                    detailsButton={messages.detailsCopy}
                    detailsButtonClick={copyOnClick}
                    additionalInfo={additionalInfo}
                />
            </Row>
        );
    };

    const renderAmount = (label, value) => (
        <Row indent="24px">
            <DetailsItem detailsTitle={label} detailsText={value} additionalInfo={amountDescription} />
        </Row>
    );

    const shareDetails = (title, text) => {
        navigator
            .share({ title, text })
            .then(() => logInfo('Successful share'))
            .catch((error) => logInfo('Error sharing', error));
    };

    const share = (e) => {
        e.preventDefault();
        shareDetails(messages.accountDetails, sharedDetails);
    };

    const renderShareDetails = () => {
        return (
            __isBrowser__ &&
            navigator &&
            navigator.share && (
                <Text variant="button" color="primary" size="secondary" onClick={share}>
                    {messages.detailsShare}
                </Text>
            )
        );
    };

    const handleNotNowClick = () => {
        setStatusPage('notNow');
    };

    const handleIPaidClick = () => {
        setStatusPage('iPaid');
    };

    if (statusPage) {
        return (
            <PaymentStatus
                icon={statusPageMessagesMap[statusPage].icon}
                title={statusPageMessagesMap[statusPage].title}
                body={statusPageMessagesMap[statusPage].body}
                buttonCaption={gotIt}
            />
        );
    }

    return (
        <CommonPage
            hasBottomPanel
            showChat={false}
            rightHeaderComponent={
                <Button type="link" size="tight" variant="tertiary" onClick={handleNotNowClick} caption={notNowButton} title={notNowButton} />
            }
        >
            <HeaderPanel title={title} subtitle={textShareDescription} />
            {renderAmount(messages.amountLabel, amount)}
            {renderField(messages.detailsName, companyName)}
            {renderField(messages.ibanLabel, formattedIban)}
            {renderField(messages.bicLabel, formattedBic)}
            {getReference(reference, paymentId) &&
                renderField(bankTransferReferenceInfo, String(getReference(reference, paymentId)), bankTransferReferenceAdditionalInfo)}
            {renderShareDetails()}
            <FixedBottomPanel isRightAligned>
                <Button type="link" raised={true} onClick={handleIPaidClick} caption={paidButton} title={paidButton} />
            </FixedBottomPanel>
        </CommonPage>
    );
};

export default connect(null, { toggleSnackBar })(BasePage(GTAccountDetailsPage));
